/**
 * Editing what each role in a crew may do.
 *
 * Three fixed roles with hard-coded rules were fine until somebody wanted a
 * Recruiter who answers join requests but cannot remove anybody, or a role
 * that can talk but not post pictures. So roles are rows and permissions are
 * switches, and this is where they are edited.
 *
 * Three things the screen has to make obvious, because getting them wrong is
 * how a crew locks itself out:
 *
 *   - **The owner is not editable.** They can do everything, always. The
 *     column is shown so the comparison makes sense, with a lock rather than
 *     a switch.
 *   - **Four permissions can never be given away.** Transferring the crew,
 *     deleting it, managing roles, and managing the top role: each is a way
 *     to take the crew from its owner. They are drawn locked in every column
 *     but the owner's, with the reason on hover.
 *   - **Rank is the order of the list.** A role may only act on roles below
 *     it, so moving a row up genuinely changes who can touch whom, and the
 *     list says so rather than hiding it behind a number.
 *
 * Everything is a draft until Save: a permission screen that writes on every
 * click is a permission screen where a mis-click is a live change.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Tip } from './HoverCard';
import { Avatar } from './Avatar';
import { Button, Input, useConfirm, useToast } from './ui';
import { Switch } from './Switch';
import type { Crew } from './Crew';

export type CrewRole = {
  id: string;
  name: string;
  colour: string;
  rank: number;
  system: boolean;
  grants: string[];
};

type Permission = { id: string; group: string; name: string; note: string; owner?: boolean };
type Catalogue = {
  permissions: Permission[];
  groups: string[];
  ownerOnly: string[];
  maxRoles: number;
  defaults?: CrewRole[];
};

const COLOURS = ['#6fc48d', '#a98bff', '#e6bd68', '#5fa8ff', '#ff8ab8', '#d08a4e', '#8d97a8', '#4fe3b0'];

export function CrewRoles({ crew, onChanged }: { crew: Crew; onChanged: () => void }) {
  const catalog = useSession((s) => s.catalog);
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const owner = crew.myRole === 'owner';

  const cat: Catalogue = (catalog as unknown as { crewRoles?: Catalogue })?.crewRoles ?? FALLBACK;
  const [roles, setRoles] = useState<CrewRole[]>(() => (crew.roles?.length ? crew.roles : cat.defaults ?? DEFAULTS));
  const [picked, setPicked] = useState<string>(() => (crew.roles?.[0]?.id ?? 'officer'));
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  /*
   * Take the server's copy whenever it changes and there is nothing unsaved.
   *
   * Without this the editor was a snapshot from the moment it mounted: the
   * save worked, the crew came back with the new role, and the screen kept
   * showing what it had started with - which read as the role disappearing.
   */
  const signature = JSON.stringify(crew.roles ?? []);
  const lastSeen = useRef(signature);
  useEffect(() => {
    if (signature === lastSeen.current) return;
    lastSeen.current = signature;
    if (dirty) return;
    if (crew.roles?.length) setRoles(crew.roles);
    // dirty is read, not depended on: re-running when it flips would pull the
    // server's copy in the instant somebody stops editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const role = roles.find((r) => r.id === picked) ?? roles[0];
  const byGroup = useMemo(() => {
    const m = new Map<string, Permission[]>();
    for (const p of cat.permissions) m.set(p.group, [...(m.get(p.group) ?? []), p]);
    return m;
  }, [cat.permissions]);

  /* Who holds each role, so "2 members" beside a row is a real number. */
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const x of crew.members) m.set(x.role, (m.get(x.role) ?? 0) + 1);
    return m;
  }, [crew.members]);

  function edit(id: string, patch: Partial<CrewRole>) {
    setRoles((list) => list.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setDirty(true);
  }

  function toggle(id: string, permission: string, on: boolean) {
    setRoles((list) => list.map((r) => {
      if (r.id !== id) return r;
      const grants = on ? [...new Set([...r.grants, permission])] : r.grants.filter((g) => g !== permission);
      return { ...r, grants };
    }));
    setDirty(true);
  }

  function addRole() {
    if (roles.length >= cat.maxRoles) { toast('That is as many roles as a crew can have', 'warn'); return; }
    const id = 'role' + Math.random().toString(36).slice(2, 7);
    const next: CrewRole = {
      id,
      name: 'New role',
      colour: COLOURS[roles.length % COLOURS.length]!,
      rank: roles.length,
      system: false,
      /* Starts as a quiet member: adding a role should not hand anybody
         power by accident. */
      grants: ['chat.read', 'chat.write', 'chat.react', 'voice.join', 'voice.speak']
    };
    /* Above the default role, below everything else: a new role should be
       able to do more than a plain member and less than an officer, which is
       what people mean when they add one. */
    setRoles((list) => {
      const at = Math.max(0, list.findIndex((r) => r.id === 'member'));
      const out = list.slice();
      out.splice(at, 0, next);
      return out.map((r, i) => ({ ...r, rank: i + 1 }));
    });
    setPicked(id);
    setDirty(true);
  }

  async function removeRole(id: string) {
    const held = counts.get(id) ?? 0;
    const ok = await confirm(
      'Delete this role?',
      held ? held + (held === 1 ? ' person holds it and becomes a Member.' : ' people hold it and become Members.')
        : 'Nobody holds it.',
      { confirmLabel: 'Delete role', danger: true }
    );
    if (!ok) return;
    setRoles((list) => list.filter((r) => r.id !== id).map((r, i) => ({ ...r, rank: i + 1 })));
    setPicked('officer');
    setDirty(true);
  }

  function move(id: string, by: number) {
    setRoles((list) => {
      const i = list.findIndex((r) => r.id === id);
      const j = i + by;
      if (i < 0 || j < 0 || j >= list.length) return list;
      const next = list.slice();
      const [row] = next.splice(i, 1);
      next.splice(j, 0, row!);
      return next.map((r, k) => ({ ...r, rank: k + 1 }));
    });
    setDirty(true);
  }

  async function save() {
    setBusy(true);
    try {
      await api.put('/api/crews/' + crew.id + '/roles', { roles });
      onChanged();
      setDirty(false);
      toast('Roles saved', 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {dialog}

      {!owner ? (
        <p className="m-0 flex items-start gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 p-3 text-[13px] leading-snug text-mute">
          <Icon name="lock" size={15} className="mt-px flex-none text-ghost" />
          Only the owner can change roles. This is what they currently allow.
        </p>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-[280px_minmax(0,1fr)]">
        {/* ---- the roles, in rank order ---- */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <h4 className="m-0 flex-1 text-[14px] font-bold text-ink">Roles</h4>
            {owner ? (
              <Button size="sm" kind="ghost" icon={<Icon name="plus" size={14} />} onClick={addRole}>Add</Button>
            ) : null}
          </div>
          <p className="m-0 mb-1 text-[11.5px] leading-snug text-ghost">
            Highest first. A role can only act on the roles below it.
          </p>

          <RoleRow
            colour="#e6bd68"
            name="Owner"
            count={1}
            locked
            on={false}
            onPick={() => { /* the owner is not editable */ }}
          />

          {roles.map((r, i) => (
            <RoleRow
              key={r.id}
              colour={r.colour}
              name={r.name}
              count={counts.get(r.id) ?? 0}
              on={picked === r.id}
              onPick={() => setPicked(r.id)}
              onUp={owner && i > 0 ? () => move(r.id, -1) : undefined}
              onDown={owner && i < roles.length - 1 ? () => move(r.id, 1) : undefined}
            />
          ))}
        </div>

        {/* ---- the role being edited ---- */}
        {role ? (
          <div className="min-w-0">
            <section className="rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-3.5">
              <div className="flex flex-wrap items-end gap-3">
                <label className="min-w-[180px] flex-1">
                  <span className="mb-1 block text-[12px] text-mute">Role name</span>
                  <Input
                    value={role.name}
                    disabled={!owner}
                    maxLength={24}
                    onChange={(e) => edit(role.id, { name: e.target.value })}
                  />
                </label>
                <div>
                  <span className="mb-1 block text-[12px] text-mute">Colour</span>
                  <div className="flex gap-1.5">
                    {COLOURS.map((c) => (
                      <Tip key={c} text={'Use ' + c}>
                        <button
                          type="button"
                          disabled={!owner}
                          onClick={() => edit(role.id, { colour: c })}
                          className={cn(
                            'h-7 w-7 rounded-full outline-none transition-transform',
                            role.colour === c ? 'scale-110 shadow-[0_0_0_2px_var(--bg-2),0_0_0_4px_var(--accent)]' : 'hover:scale-110'
                          )}
                          style={{ background: c }}
                        />
                      </Tip>
                    ))}
                  </div>
                </div>
                {owner && !role.system ? (
                  <Button size="sm" kind="ghost" onClick={() => void removeRole(role.id)}>Delete role</Button>
                ) : null}
              </div>
              {role.system ? (
                <p className="m-0 mt-2.5 flex items-center gap-1.5 text-[11.5px] text-ghost">
                  <Icon name="info" size={12} />
                  {role.id === 'officer'
                    ? 'The top role. It cannot be deleted - something has to sit below the owner.'
                    : 'What a new member becomes. It cannot be deleted.'}
                </p>
              ) : null}
            </section>

            {/* ---- the switches ---- */}
            <div className="mt-3 flex flex-col gap-3">
              {cat.groups.map((group) => (
                <section key={group} className="overflow-hidden rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2">
                  <h5 className="m-0 border-b border-line-soft px-3.5 py-2.5 text-[11.5px] font-semibold tracking-[.07em] text-ghost uppercase">
                    {group}
                  </h5>
                  <ul className="m-0 flex list-none flex-col p-0">
                    {(byGroup.get(group) ?? []).map((p) => {
                      const locked = !!p.owner;
                      const on = locked ? false : role.grants.includes(p.id);
                      return (
                        <li key={p.id} className="flex items-start gap-3 border-b border-line-soft px-3.5 py-2.5 last:border-b-0">
                          <span className="min-w-0 flex-1">
                            <span className="block text-[13.5px] font-semibold text-ink">{p.name}</span>
                            <span className="block text-[12px] leading-snug text-mute">{p.note}</span>
                          </span>
                          {locked ? (
                            <Tip text="The owner's alone" sub="Granting this would be a way to take the crew from its owner.">
                              <span className="mt-0.5 flex flex-none items-center gap-1.5 text-[11.5px] font-semibold text-ghost">
                                <Icon name="lock" size={13} />
                                Owner only
                              </span>
                            </Tip>
                          ) : (
                            <span className="mt-0.5 flex-none">
                              <Switch
                                size="sm"
                                checked={on}
                                disabled={!owner}
                                onChange={(v) => toggle(role.id, p.id, v)}
                                label={p.name}
                              />
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>

            {/* ---- who holds it ---- */}
            <section className="mt-3 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-3.5">
              <h5 className="m-0 mb-2.5 text-[13px] font-bold text-ink">
                Who holds it
                <span className="ml-2 rounded-full bg-surface-3 px-2 py-px text-[11.5px] font-semibold text-dim tabular-nums">
                  {counts.get(role.id) ?? 0}
                </span>
              </h5>
              <div className="flex flex-wrap gap-2">
                {crew.members.filter((m) => m.role === role.id).map((m) => (
                  <span key={m.user.id} className="flex items-center gap-2 rounded-full bg-surface-3 py-1 pr-3 pl-1">
                    <Avatar user={m.user} size={22} />
                    <span className="text-[12.5px] font-semibold text-dim">{m.user.displayName}</span>
                  </span>
                ))}
                {!(counts.get(role.id) ?? 0) ? (
                  <span className="text-[12.5px] text-mute">Nobody yet. Give it to somebody from Members.</span>
                ) : null}
              </div>
            </section>
          </div>
        ) : null}
      </div>

      {owner && dirty ? (
        <div className="sticky bottom-0 flex items-center gap-3 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-1 p-3 shadow-[0_-6px_18px_-12px_rgba(0,0,0,.6),inset_0_0_0_1px_var(--line-soft)]">
          <span className="flex items-center gap-2 text-[13px] font-semibold text-warn">
            <Icon name="alert" size={15} />
            Unsaved permission changes
          </span>
          <span className="flex-1" />
          <Button kind="ghost" disabled={busy} onClick={() => { setRoles(crew.roles?.length ? crew.roles : (cat.defaults ?? DEFAULTS)); setDirty(false); }}>
            Discard
          </Button>
          <Button kind="primary" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save permissions'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function RoleRow({ colour, name, count, on, locked, onPick, onUp, onDown }: {
  colour: string;
  name: string;
  count: number;
  on: boolean;
  locked?: boolean;
  onPick: () => void;
  onUp?: () => void;
  onDown?: () => void;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2.5 rounded-[calc(12px*var(--radius-mul,1))] px-3 py-2.5 transition-colors',
        on ? 'bg-accent-soft' : locked ? 'bg-surface-2' : 'bg-surface-2 hover:bg-surface-3'
      )}
    >
      <button
        type="button"
        onClick={onPick}
        disabled={locked}
        className="flex min-w-0 flex-1 items-center gap-2.5 text-left outline-none disabled:cursor-default"
      >
        <span className="h-3 w-3 flex-none rounded-full" style={{ background: colour }} />
        <span className="min-w-0 flex-1">
          <span className={cn('block truncate text-[13.5px] font-bold', on ? 'text-accent' : 'text-ink')}>{name}</span>
          <span className="block text-[11.5px] text-mute">{count} {count === 1 ? 'member' : 'members'}</span>
        </span>
        {locked ? <Icon name="lock" size={14} className="flex-none text-ghost" /> : null}
      </button>
      {onUp || onDown ? (
        <span className="flex flex-none flex-col">
          <button
            type="button"
            aria-label="Move up"
            disabled={!onUp}
            onClick={onUp}
            className="grid h-4 w-5 place-items-center rounded text-ghost outline-none transition-colors hover:text-ink disabled:opacity-30"
          >
            <Icon name="chevronD" size={11} className="rotate-180" />
          </button>
          <button
            type="button"
            aria-label="Move down"
            disabled={!onDown}
            onClick={onDown}
            className="grid h-4 w-5 place-items-center rounded text-ghost outline-none transition-colors hover:text-ink disabled:opacity-30"
          >
            <Icon name="chevronD" size={11} />
          </button>
        </span>
      ) : null}
    </div>
  );
}

const DEFAULTS: CrewRole[] = [
  { id: 'officer', name: 'Officer', colour: '#6fc48d', rank: 1, system: true, grants: [] },
  { id: 'member', name: 'Member', colour: '#8d97a8', rank: 2, system: true, grants: [] }
];

const FALLBACK: Catalogue & { defaults?: CrewRole[] } = {
  permissions: [],
  groups: [],
  ownerOnly: [],
  maxRoles: 8,
  defaults: DEFAULTS
};
